// @src/pre-deployment/src/data/grammarSchema.js v1.0
// - Comprehensive grammar schema for Level 2
// - Defines all 10 chapters with 3 grammar points each
// - Supports dynamic exercise generation
// - Medical vocabulary context for Level 2

export const LEVEL_2_GRAMMAR_SCHEMA = {
  level: 2,
  title: "Elementary Korean Grammar",
  description: "Essential grammar patterns for everyday Korean communication",
  totalChapters: 10,
  totalGrammarPoints: 30,
  
  chapters: {
    1: {
      title: "Basic Actions and Time",
      description: "Learn to express ongoing actions, sequence, and intentions",
      grammarPoints: [
        {
          id: "ch1_gmr_001",
          grammarPoint: "-고 있다",
          chapter: "1과",
          usage: "Indicates an action is in progress",
          explanation: "This pattern is used to express that an action is currently happening or in progress. It's equivalent to the English present continuous tense.",
          nuance: "Used for actions that are happening right now or over a period of time",
          difficulty: "beginner",
          category: "progressive",
          examples: [
            "저는 지금 병원에서 일하고 있어요 (I am working at the hospital now)",
            "의사 선생님이 환자를 진찰하고 있어요 (The doctor is examining the patient)",
            "간호사가 약을 준비하고 있어요 (The nurse is preparing medicine)",
            "환자가 기다리고 있어요 (The patient is waiting)"
          ],
          medicalContext: {
            vocabulary: ["병원", "의사", "간호사", "환자", "진찰", "약", "기다리다"],
            scenarios: ["hospital visit", "medical examination", "treatment"]
          },
          exerciseTypes: ["cloze", "sentence_building", "translation"]
        },
        {
          id: "ch1_gmr_002", 
          grammarPoint: "-(으)ㄴ 후에",
          chapter: "1과",
          usage: "Indicates 'after' completing an action",
          explanation: "This pattern expresses the sequence of events, meaning 'after doing something'. It shows that one action happens after another is completed.",
          nuance: "Emphasizes the completion of the first action before the second begins",
          difficulty: "beginner",
          category: "temporal",
          examples: [
            "검사를 받은 후에 결과를 알려드릴게요 (I'll tell you the results after the examination)",
            "약을 먹은 후에 휴식을 취하세요 (Please rest after taking the medicine)",
            "수술을 받은 후에 회복해야 해요 (You need to recover after the surgery)",
            "진찰을 받은 후에 처방전을 받았어요 (I received a prescription after the examination)"
          ],
          medicalContext: {
            vocabulary: ["검사", "결과", "약", "휴식", "수술", "회복", "진찰", "처방전"],
            scenarios: ["post-treatment", "follow-up care", "recovery"]
          },
          exerciseTypes: ["cloze", "sequence_ordering", "translation"]
        },
        {
          id: "ch1_gmr_003",
          grammarPoint: "-(으)려고 하다", 
          chapter: "1과",
          usage: "Expresses a plan or intention to do something",
          explanation: "This pattern indicates the speaker's intention or plan to do something. It's used to express future plans or goals.",
          nuance: "Shows determination or preparation for an action",
          difficulty: "beginner",
          category: "intention",
          examples: [
            "내일 병원에 가려고 해요 (I plan to go to the hospital tomorrow)",
            "건강검진을 받으려고 해요 (I intend to get a health checkup)",
            "의사와 상담하려고 해요 (I plan to consult with the doctor)",
            "운동을 시작하려고 해요 (I plan to start exercising)"
          ],
          medicalContext: {
            vocabulary: ["병원", "건강검진", "의사", "상담", "운동"],
            scenarios: ["appointment scheduling", "health planning", "preventive care"]
          },
          exerciseTypes: ["cloze", "intention_matching", "translation"]
        }
      ]
    },
    
    2: {
      title: "Location and Sequence",
      description: "Learn to express location and sequential actions",
      grammarPoints: [
        {
          id: "ch2_gmr_001",
          grammarPoint: "-에 있다",
          chapter: "2과", 
          usage: "Indicates the location of a person or object",
          explanation: "This pattern is used to express where someone or something is located. It's the basic way to indicate position or location.",
          nuance: "Static location, not movement",
          difficulty: "beginner",
          category: "location",
          examples: [
            "의사 선생님이 진료실에 있어요 (The doctor is in the examination room)",
            "응급실이 1층에 있어요 (The emergency room is on the first floor)",
            "약국이 병원 옆에 있어요 (The pharmacy is next to the hospital)",
            "환자가 대기실에 있어요 (The patient is in the waiting room)"
          ],
          medicalContext: {
            vocabulary: ["진료실", "응급실", "약국", "대기실", "병원"],
            scenarios: ["hospital navigation", "finding facilities", "patient location"]
          },
          exerciseTypes: ["cloze", "location_matching", "translation"]
        },
        {
          id: "ch2_gmr_002",
          grammarPoint: "-아서/어서/여서",
          chapter: "2과",
          usage: "Indicates that two actions are closely related and occur in sequence",
          explanation: "This pattern connects two actions that happen in sequence, where the first action leads to or causes the second action.",
          nuance: "Shows cause and effect or natural sequence",
          difficulty: "intermediate",
          category: "sequence",
          examples: [
            "병원에 가서 진찰을 받았어요 (I went to the hospital and got examined)",
            "약을 먹어서 아픔이 줄었어요 (I took medicine and the pain decreased)",
            "운동을 해서 건강해졌어요 (I exercised and became healthy)",
            "검사를 받아서 결과를 알았어요 (I got tested and found out the results)"
          ],
          medicalContext: {
            vocabulary: ["병원", "진찰", "약", "아픔", "운동", "건강", "검사", "결과"],
            scenarios: ["treatment process", "health improvement", "medical procedures"]
          },
          exerciseTypes: ["cloze", "sequence_ordering", "translation"]
        },
        {
          id: "ch2_gmr_003",
          grammarPoint: "-(으)면 되다",
          chapter: "2과",
          usage: "Indicates that a certain condition is sufficient or all that is needed",
          explanation: "This pattern expresses that doing something is enough or sufficient to achieve a goal. It's used to give advice or instructions.",
          nuance: "Shows that something is the right or sufficient action",
          difficulty: "intermediate",
          category: "sufficiency",
          examples: [
            "약을 먹으면 돼요 (Just take the medicine)",
            "휴식을 취하면 돼요 (Just rest)",
            "운동을 하면 돼요 (Just exercise)",
            "의사와 상담하면 돼요 (Just consult with the doctor)"
          ],
          medicalContext: {
            vocabulary: ["약", "휴식", "운동", "의사", "상담"],
            scenarios: ["medical advice", "treatment instructions", "health recommendations"]
          },
          exerciseTypes: ["cloze", "advice_matching", "translation"]
        }
      ]
    },
    
    3: {
      title: "Time and Gerunds",
      description: "Learn to express time periods and turn verbs into nouns",
      grammarPoints: [
        {
          id: "ch3_gmr_001",
          grammarPoint: "-(으)ㄹ 때",
          chapter: "3과",
          usage: "Indicates the moment or period of time in which an action or condition occurs",
          explanation: "This pattern expresses 'when' something happens. It indicates the time or situation when an action occurs.",
          nuance: "Specific time or situation",
          difficulty: "intermediate",
          category: "temporal",
          examples: [
            "아플 때 병원에 가세요 (Go to the hospital when you're sick)",
            "약을 먹을 때 물을 많이 마셔요 (Drink a lot of water when taking medicine)",
            "운동할 때 조심하세요 (Be careful when exercising)",
            "검사를 받을 때 긴장하지 마세요 (Don't be nervous when getting tested)"
          ],
          medicalContext: {
            vocabulary: ["아프다", "병원", "약", "물", "운동", "검사", "긴장"],
            scenarios: ["medical advice", "safety instructions", "procedure guidance"]
          },
          exerciseTypes: ["cloze", "time_matching", "translation"]
        },
        {
          id: "ch3_gmr_002",
          grammarPoint: "-는 것",
          chapter: "3과",
          usage: "Turns a verb into a gerund (a noun meaning 'the act of V-ing')",
          explanation: "This pattern converts verbs into nouns, creating gerunds that can be used as subjects or objects in sentences.",
          nuance: "Abstract concept of the action",
          difficulty: "intermediate",
          category: "nominalization",
          examples: [
            "운동하는 것이 건강에 좋아요 (Exercising is good for health)",
            "약을 먹는 것이 중요해요 (Taking medicine is important)",
            "검사를 받는 것이 필요해요 (Getting tested is necessary)",
            "의사와 상담하는 것이 도움이 돼요 (Consulting with a doctor is helpful)"
          ],
          medicalContext: {
            vocabulary: ["운동", "건강", "약", "검사", "의사", "상담", "도움"],
            scenarios: ["health advice", "medical importance", "treatment benefits"]
          },
          exerciseTypes: ["cloze", "gerund_formation", "translation"]
        },
        {
          id: "ch3_gmr_003",
          grammarPoint: "-(으)러 가다",
          chapter: "3과",
          usage: "Indicates moving somewhere for the purpose of doing an action",
          explanation: "This pattern expresses the purpose of going somewhere. It shows that you're going to a place to do something specific.",
          nuance: "Purpose of movement",
          difficulty: "intermediate",
          category: "purpose",
          examples: [
            "병원에 진찰받으러 가요 (I'm going to the hospital to get examined)",
            "약국에 약을 사러 가요 (I'm going to the pharmacy to buy medicine)",
            "체육관에 운동하러 가요 (I'm going to the gym to exercise)",
            "의사에게 상담받으러 가요 (I'm going to the doctor to get consultation)"
          ],
          medicalContext: {
            vocabulary: ["병원", "진찰", "약국", "약", "체육관", "운동", "의사", "상담"],
            scenarios: ["medical appointments", "health activities", "treatment visits"]
          },
          exerciseTypes: ["cloze", "purpose_matching", "translation"]
        }
      ]
    },
    
    4: {
      title: "Conditions and Permissions",
      description: "Learn to express conditions, permissions, and prohibitions",
      grammarPoints: [
        {
          id: "ch4_gmr_001",
          grammarPoint: "-(으)면",
          chapter: "4과",
          usage: "Indicates a condition or assumption for the following clause",
          explanation: "This pattern expresses 'if' or 'when' as a condition. It sets up a hypothetical or conditional situation.",
          nuance: "Conditional or hypothetical",
          difficulty: "intermediate",
          category: "conditional",
          examples: [
            "아프면 병원에 가세요 (If you're sick, go to the hospital)",
            "약을 먹으면 나아질 거예요 (If you take medicine, you'll get better)",
            "운동하면 건강해져요 (If you exercise, you'll become healthy)",
            "검사를 받으면 결과를 알 수 있어요 (If you get tested, you can know the results)"
          ],
          medicalContext: {
            vocabulary: ["아프다", "병원", "약", "나아지다", "운동", "건강", "검사", "결과"],
            scenarios: ["medical advice", "health conditions", "treatment outcomes"]
          },
          exerciseTypes: ["cloze", "conditional_matching", "translation"]
        },
        {
          id: "ch4_gmr_002",
          grammarPoint: "-아도/어도/여도 되다",
          chapter: "4과",
          usage: "Used to ask for or give permission to do something",
          explanation: "This pattern is used to ask for permission or to give permission. It's equivalent to 'may I' or 'it's okay to'.",
          nuance: "Permission or allowance",
          difficulty: "intermediate",
          category: "permission",
          examples: [
            "약을 먹어도 돼요? (May I take the medicine?)",
            "운동해도 돼요? (May I exercise?)",
            "휴식해도 돼요 (You may rest)",
            "의사와 상담해도 돼요 (You may consult with the doctor)"
          ],
          medicalContext: {
            vocabulary: ["약", "운동", "휴식", "의사", "상담"],
            scenarios: ["asking permission", "medical clearance", "treatment approval"]
          },
          exerciseTypes: ["cloze", "permission_matching", "translation"]
        },
        {
          id: "ch4_gmr_003",
          grammarPoint: "-(으)면 안 되다",
          chapter: "4과",
          usage: "Used to prohibit or restrict an action",
          explanation: "This pattern expresses prohibition or restriction. It's used to say that something is not allowed or should not be done.",
          nuance: "Prohibition or restriction",
          difficulty: "intermediate",
          category: "prohibition",
          examples: [
            "약을 너무 많이 먹으면 안 돼요 (You shouldn't take too much medicine)",
            "운동을 너무 많이 하면 안 돼요 (You shouldn't exercise too much)",
            "술을 마시면 안 돼요 (You shouldn't drink alcohol)",
            "늦게 자면 안 돼요 (You shouldn't sleep late)"
          ],
          medicalContext: {
            vocabulary: ["약", "운동", "술", "자다"],
            scenarios: ["medical restrictions", "health warnings", "treatment limitations"]
          },
          exerciseTypes: ["cloze", "prohibition_matching", "translation"]
        }
      ]
    },
    
    5: {
      title: "Contrast and Desires",
      description: "Learn to express contrast, desires, and noun modification",
      grammarPoints: [
        {
          id: "ch5_gmr_001",
          grammarPoint: "-지만",
          chapter: "5과",
          usage: "Used to make a statement that contradicts or contrasts with what was just said",
          explanation: "This pattern expresses contrast or contradiction. It's equivalent to 'but' or 'however' in English.",
          nuance: "Contrast or contradiction",
          difficulty: "intermediate",
          category: "contrast",
          examples: [
            "아프지만 병원에 안 갔어요 (I'm sick but I didn't go to the hospital)",
            "약을 먹었지만 아직 아파요 (I took medicine but I'm still sick)",
            "운동을 했지만 건강이 안 좋아요 (I exercised but my health isn't good)",
            "검사를 받았지만 결과가 안 나왔어요 (I got tested but the results didn't come out)"
          ],
          medicalContext: {
            vocabulary: ["아프다", "병원", "약", "운동", "건강", "검사", "결과"],
            scenarios: ["medical contradictions", "treatment outcomes", "health conditions"]
          },
          exerciseTypes: ["cloze", "contrast_matching", "translation"]
        },
        {
          id: "ch5_gmr_002",
          grammarPoint: "-(으)면 좋겠다",
          chapter: "5과",
          usage: "Expresses the speaker's desire, wish, or expectation",
          explanation: "This pattern expresses the speaker's wish or hope. It's used to express what you want or hope will happen.",
          nuance: "Wish or hope",
          difficulty: "intermediate",
          category: "desire",
          examples: [
            "빨리 나으면 좋겠어요 (I hope I get better quickly)",
            "건강해지면 좋겠어요 (I hope I become healthy)",
            "검사 결과가 좋으면 좋겠어요 (I hope the test results are good)",
            "의사가 친절하면 좋겠어요 (I hope the doctor is kind)"
          ],
          medicalContext: {
            vocabulary: ["나아지다", "건강", "검사", "결과", "의사", "친절"],
            scenarios: ["health wishes", "treatment hopes", "medical expectations"]
          },
          exerciseTypes: ["cloze", "desire_matching", "translation"]
        },
        {
          id: "ch5_gmr_003",
          grammarPoint: "-는/(으)ㄴ",
          chapter: "5과",
          usage: "Modifies a following noun. Indicates that the action or state is in the present",
          explanation: "This pattern modifies nouns with verbs or adjectives. It creates descriptive phrases that modify the following noun.",
          nuance: "Present tense modification",
          difficulty: "advanced",
          category: "modification",
          examples: [
            "운동하는 사람이 건강해요 (People who exercise are healthy)",
            "약을 먹는 시간이 중요해요 (The time for taking medicine is important)",
            "검사를 받는 환자가 많아요 (There are many patients getting tested)",
            "의사가 하는 말을 들어보세요 (Listen to what the doctor says)"
          ],
          medicalContext: {
            vocabulary: ["운동", "사람", "건강", "약", "시간", "검사", "환자", "의사", "말"],
            scenarios: ["medical descriptions", "health observations", "treatment contexts"]
          },
          exerciseTypes: ["cloze", "modification_matching", "translation"]
        }
      ]
    },
    
    6: {
      title: "Honorifics and Respect",
      description: "Learn to use honorific language for respect and politeness",
      grammarPoints: [
        {
          id: "ch6_gmr_001",
          grammarPoint: "높임말",
          chapter: "6과",
          usage: "Honorifics - Used when the subject is older or of higher status",
          explanation: "Honorific language is used to show respect to people who are older or of higher status. It's essential in Korean culture.",
          nuance: "Respect and politeness",
          difficulty: "advanced",
          category: "honorific",
          examples: [
            "의사 선생님이 진찰하십니다 (The doctor examines [honorific])",
            "환자분이 기다리십니다 (The patient waits [honorific])",
            "간호사분이 도와주십니다 (The nurse helps [honorific])",
            "선생님이 설명해주십니다 (The teacher explains [honorific])"
          ],
          medicalContext: {
            vocabulary: ["의사", "선생님", "진찰", "환자", "간호사", "도움", "설명"],
            scenarios: ["medical respect", "professional communication", "polite interactions"]
          },
          exerciseTypes: ["cloze", "honorific_matching", "translation"]
        },
        {
          id: "ch6_gmr_002",
          grammarPoint: "-(으)세요",
          chapter: "6과",
          usage: "A present tense honorific ending",
          explanation: "This is a polite present tense ending used when speaking to someone older or of higher status.",
          nuance: "Polite present tense",
          difficulty: "advanced",
          category: "honorific",
          examples: [
            "어디가 아프세요? (Where does it hurt? [polite])",
            "언제부터 아프셨어요? (Since when have you been sick? [polite])",
            "어떤 증상이 있으세요? (What symptoms do you have? [polite])",
            "약을 드셔보세요 (Please try taking the medicine [polite])"
          ],
          medicalContext: {
            vocabulary: ["아프다", "증상", "약", "드시다"],
            scenarios: ["medical consultation", "polite questioning", "respectful communication"]
          },
          exerciseTypes: ["cloze", "polite_matching", "translation"]
        },
        {
          id: "ch6_gmr_003",
          grammarPoint: "-(으)실 거예요",
          chapter: "6과",
          usage: "A future tense honorific ending for plans or schedules",
          explanation: "This is a polite future tense ending used when talking about someone else's future plans or actions.",
          nuance: "Polite future tense",
          difficulty: "advanced",
          category: "honorific",
          examples: [
            "내일 병원에 가실 거예요 (You will go to the hospital tomorrow [polite])",
            "검사를 받으실 거예요 (You will get tested [polite])",
            "의사와 상담하실 거예요 (You will consult with the doctor [polite])",
            "약을 드실 거예요 (You will take the medicine [polite])"
          ],
          medicalContext: {
            vocabulary: ["병원", "검사", "의사", "상담", "약"],
            scenarios: ["future medical plans", "polite predictions", "respectful scheduling"]
          },
          exerciseTypes: ["cloze", "future_honorific_matching", "translation"]
        }
      ]
    },
    
    7: {
      title: "Experience and Discovery",
      description: "Learn to express trying things and making discoveries",
      grammarPoints: [
        {
          id: "ch7_gmr_001",
          grammarPoint: "-아/어/여 보다",
          chapter: "7과",
          usage: "Indicates trying or experiencing an action",
          explanation: "This pattern expresses trying something or having an experience. It's used to suggest trying something or to describe an experience.",
          nuance: "Trying or experiencing",
          difficulty: "intermediate",
          category: "experience",
          examples: [
            "이 약을 먹어보세요 (Try taking this medicine)",
            "운동을 해보세요 (Try exercising)",
            "의사와 상담해보세요 (Try consulting with a doctor)",
            "검사를 받아보세요 (Try getting tested)"
          ],
          medicalContext: {
            vocabulary: ["약", "운동", "의사", "상담", "검사"],
            scenarios: ["medical suggestions", "treatment recommendations", "health advice"]
          },
          exerciseTypes: ["cloze", "experience_matching", "translation"]
        },
        {
          id: "ch7_gmr_002",
          grammarPoint: "-(으)ㄴ",
          chapter: "7과",
          usage: "Attaches to verb stems to modify a noun, indicating the action occurred in the past",
          explanation: "This pattern modifies nouns with past tense verbs. It describes something that happened in the past.",
          nuance: "Past tense modification",
          difficulty: "advanced",
          category: "modification",
          examples: [
            "어제 먹은 약이 효과가 있어요 (The medicine I took yesterday is effective)",
            "지난 주에 받은 검사 결과가 나왔어요 (The test results from last week came out)",
            "작년에 만난 의사가 친절했어요 (The doctor I met last year was kind)",
            "지난 달에 한 운동이 도움이 됐어요 (The exercise I did last month was helpful)"
          ],
          medicalContext: {
            vocabulary: ["약", "효과", "검사", "결과", "의사", "친절", "운동", "도움"],
            scenarios: ["past medical experiences", "treatment history", "health records"]
          },
          exerciseTypes: ["cloze", "past_modification_matching", "translation"]
        },
        {
          id: "ch7_gmr_003",
          grammarPoint: "-네요",
          chapter: "7과",
          usage: "Indicates a new realization or discovery; also used to state an opinion softly",
          explanation: "This pattern expresses a new realization or discovery. It's also used to state an opinion in a soft, non-confrontational way.",
          nuance: "New realization or soft opinion",
          difficulty: "intermediate",
          category: "realization",
          examples: [
            "아프네요 (You seem sick)",
            "건강해 보이네요 (You look healthy)",
            "검사 결과가 좋네요 (The test results look good)",
            "의사가 친절하네요 (The doctor seems kind)"
          ],
          medicalContext: {
            vocabulary: ["아프다", "건강", "검사", "결과", "의사", "친절"],
            scenarios: ["medical observations", "health assessments", "polite comments"]
          },
          exerciseTypes: ["cloze", "realization_matching", "translation"]
        }
      ]
    },
    
    8: {
      title: "Promises and Assumptions",
      description: "Learn to express promises and make assumptions",
      grammarPoints: [
        {
          id: "ch8_gmr_001",
          grammarPoint: "-(으)ㄹ게요",
          chapter: "8과",
          usage: "Expresses a promise or decision to do something for the listener",
          explanation: "This pattern expresses a promise or decision to do something, especially for the benefit of the listener.",
          nuance: "Promise or decision for listener",
          difficulty: "intermediate",
          category: "promise",
          examples: [
            "내일 병원에 가서 진찰받을게요 (I'll go to the hospital tomorrow and get examined)",
            "약을 제대로 먹을게요 (I'll take the medicine properly)",
            "운동을 열심히 할게요 (I'll exercise hard)",
            "의사 말씀을 잘 들을게요 (I'll listen well to the doctor's words)"
          ],
          medicalContext: {
            vocabulary: ["병원", "진찰", "약", "운동", "의사", "말씀"],
            scenarios: ["medical promises", "health commitments", "treatment compliance"]
          },
          exerciseTypes: ["cloze", "promise_matching", "translation"]
        },
        {
          id: "ch8_gmr_002",
          grammarPoint: "-는 것 같다/(으)ㄴ 것 같다",
          chapter: "8과",
          usage: "Expresses an assumption about a fact or condition",
          explanation: "This pattern expresses an assumption or guess about a situation. It's used when you're not certain but have an opinion.",
          nuance: "Assumption or guess",
          difficulty: "advanced",
          category: "assumption",
          examples: [
            "아픈 것 같아요 (It seems like you're sick)",
            "건강한 것 같아요 (It seems like you're healthy)",
            "검사 결과가 좋은 것 같아요 (The test results seem good)",
            "의사가 바쁜 것 같아요 (The doctor seems busy)"
          ],
          medicalContext: {
            vocabulary: ["아프다", "건강", "검사", "결과", "의사", "바쁘다"],
            scenarios: ["medical assumptions", "health observations", "professional assessments"]
          },
          exerciseTypes: ["cloze", "assumption_matching", "translation"]
        },
        {
          id: "ch8_gmr_003",
          grammarPoint: "-(으)ㄹ 것이다",
          chapter: "8과",
          usage: "Expresses a guess or prediction about the future",
          explanation: "This pattern expresses a prediction or guess about what will happen in the future.",
          nuance: "Future prediction or guess",
          difficulty: "advanced",
          category: "prediction",
          examples: [
            "내일 나을 것이다 (I will probably get better tomorrow)",
            "검사 결과가 좋을 것이다 (The test results will probably be good)",
            "의사가 도와줄 것이다 (The doctor will probably help)",
            "약이 효과가 있을 것이다 (The medicine will probably be effective)"
          ],
          medicalContext: {
            vocabulary: ["나아지다", "검사", "결과", "의사", "도움", "약", "효과"],
            scenarios: ["medical predictions", "treatment outcomes", "health forecasts"]
          },
          exerciseTypes: ["cloze", "prediction_matching", "translation"]
        }
      ]
    },
    
    9: {
      title: "Background and Context",
      description: "Learn to provide background information and context",
      grammarPoints: [
        {
          id: "ch9_gmr_001",
          grammarPoint: "-는데/(으)ㄴ데",
          chapter: "9과",
          usage: "Provides the situation or background for the following clause",
          explanation: "This pattern provides background information or context for what follows. It sets up the situation before the main point.",
          nuance: "Background or context",
          difficulty: "advanced",
          category: "background",
          examples: [
            "아픈데 병원에 가야 해요 (I'm sick, so I need to go to the hospital)",
            "검사를 받았는데 결과가 안 나왔어요 (I got tested, but the results didn't come out)",
            "운동을 했는데 건강이 안 좋아요 (I exercised, but my health isn't good)",
            "약을 먹었는데 아직 아파요 (I took medicine, but I'm still sick)"
          ],
          medicalContext: {
            vocabulary: ["아프다", "병원", "검사", "결과", "운동", "건강", "약"],
            scenarios: ["medical explanations", "health contexts", "treatment backgrounds"]
          },
          exerciseTypes: ["cloze", "background_matching", "translation"]
        },
        {
          id: "ch9_gmr_002",
          grammarPoint: "반말 1 (-아/어/여)",
          chapter: "9과",
          usage: "Informal speech used with close friends or younger people",
          explanation: "This is informal speech used with close friends, family, or people younger than you. It's casual and friendly.",
          nuance: "Informal and casual",
          difficulty: "advanced",
          category: "informal",
          examples: [
            "아파? (Are you sick? [informal])",
            "병원에 가? (Are you going to the hospital? [informal])",
            "약 먹었어? (Did you take medicine? [informal])",
            "운동해? (Do you exercise? [informal])"
          ],
          medicalContext: {
            vocabulary: ["아프다", "병원", "약", "운동"],
            scenarios: ["casual medical talk", "friendly health questions", "informal consultations"]
          },
          exerciseTypes: ["cloze", "informal_matching", "translation"]
        },
        {
          id: "ch9_gmr_003",
          grammarPoint: "반말 2 (-야)",
          chapter: "9과",
          usage: "Informal ending for 이다 (to be) and 아니다 (to not be)",
          explanation: "This is the informal version of '이다' (to be) and '아니다' (to not be), used in casual speech.",
          nuance: "Informal 'to be'",
          difficulty: "advanced",
          category: "informal",
          examples: [
            "의사야? (Are you a doctor? [informal])",
            "환자야 (You're a patient [informal])",
            "건강해? (Are you healthy? [informal])",
            "아프지 않아? (Aren't you sick? [informal])"
          ],
          medicalContext: {
            vocabulary: ["의사", "환자", "건강", "아프다"],
            scenarios: ["casual medical identity", "informal health status", "friendly medical questions"]
          },
          exerciseTypes: ["cloze", "informal_be_matching", "translation"]
        }
      ]
    },
    
    10: {
      title: "Future and Final Patterns",
      description: "Learn final grammar patterns for future tense and completion",
      grammarPoints: [
        {
          id: "ch10_gmr_001",
          grammarPoint: "-(으)ㄹ",
          chapter: "10과",
          usage: "Modifies a noun, indicating the action will happen in the future",
          explanation: "This pattern modifies nouns with future tense verbs. It describes something that will happen in the future.",
          nuance: "Future tense modification",
          difficulty: "advanced",
          category: "modification",
          examples: [
            "내일 먹을 약을 준비했어요 (I prepared the medicine I'll take tomorrow)",
            "다음 주에 받을 검사를 예약했어요 (I made an appointment for the test I'll take next week)",
            "내년에 만날 의사를 찾고 있어요 (I'm looking for a doctor I'll meet next year)",
            "오늘 할 운동을 계획했어요 (I planned the exercise I'll do today)"
          ],
          medicalContext: {
            vocabulary: ["약", "검사", "예약", "의사", "운동", "계획"],
            scenarios: ["future medical plans", "upcoming treatments", "scheduled health activities"]
          },
          exerciseTypes: ["cloze", "future_modification_matching", "translation"]
        }
      ]
    }
  }
};

// Helper functions for working with the grammar schema
export const GrammarSchemaUtils = {
  // Get all grammar points for a specific chapter
  getGrammarPointsForChapter: (chapterNumber) => {
    return LEVEL_2_GRAMMAR_SCHEMA.chapters[chapterNumber]?.grammarPoints || [];
  },
  
  // Get a specific grammar point by ID
  getGrammarPointById: (grammarId) => {
    for (const chapter of Object.values(LEVEL_2_GRAMMAR_SCHEMA.chapters)) {
      const grammarPoint = chapter.grammarPoints.find(gp => gp.id === grammarId);
      if (grammarPoint) return grammarPoint;
    }
    return null;
  },
  
  // Get all grammar points by category
  getGrammarPointsByCategory: (category) => {
    const results = [];
    for (const chapter of Object.values(LEVEL_2_GRAMMAR_SCHEMA.chapters)) {
      const matchingPoints = chapter.grammarPoints.filter(gp => gp.category === category);
      results.push(...matchingPoints);
    }
    return results;
  },
  
  // Get all grammar points by difficulty
  getGrammarPointsByDifficulty: (difficulty) => {
    const results = [];
    for (const chapter of Object.values(LEVEL_2_GRAMMAR_SCHEMA.chapters)) {
      const matchingPoints = chapter.grammarPoints.filter(gp => gp.difficulty === difficulty);
      results.push(...matchingPoints);
    }
    return results;
  },
  
  // Generate exercise data from grammar points
  generateExercisesFromGrammar: (grammarPoint, exerciseType = 'cloze') => {
    const exercises = [];
    
    switch (exerciseType) {
      case 'cloze':
        grammarPoint.examples.forEach((example, index) => {
          // Extract the grammar pattern from the example
          const pattern = grammarPoint.grammarPoint;
          const question = example.replace(new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '__');
          
          exercises.push({
            id: `${grammarPoint.id}_cloze_${index}`,
            difficulty: grammarPoint.difficulty,
            category: grammarPoint.category,
            subcategory: grammarPoint.grammarPoint,
            question: question,
            answer: pattern,
            grammar_focus: grammarPoint.usage,
            expected_input: 'grammar_pattern',
            validation_hint: grammarPoint.explanation,
            example: example
          });
        });
        break;
        
      case 'sentence_building':
        grammarPoint.examples.forEach((example, index) => {
          const words = example.split(' ');
          exercises.push({
            id: `${grammarPoint.id}_sentence_${index}`,
            difficulty: grammarPoint.difficulty,
            category: grammarPoint.category,
            subcategory: grammarPoint.grammarPoint,
            words: words,
            correctOrder: words,
            grammar_focus: grammarPoint.usage,
            example: example
          });
        });
        break;
        
      case 'translation':
        grammarPoint.examples.forEach((example, index) => {
          exercises.push({
            id: `${grammarPoint.id}_translation_${index}`,
            difficulty: grammarPoint.difficulty,
            category: grammarPoint.category,
            subcategory: grammarPoint.grammarPoint,
            korean: example,
            english: example, // In real implementation, this would be the English translation
            grammar_focus: grammarPoint.usage
          });
        });
        break;
        
      default:
        console.warn(`Unknown exercise type: ${exerciseType}`);
        break;
    }
    
    return exercises;
  },
  
  // Get vocabulary for a specific grammar point
  getVocabularyForGrammarPoint: (grammarPoint) => {
    return grammarPoint.medicalContext?.vocabulary || [];
  },
  
  // Get scenarios for a specific grammar point
  getScenariosForGrammarPoint: (grammarPoint) => {
    return grammarPoint.medicalContext?.scenarios || [];
  }
};

export default LEVEL_2_GRAMMAR_SCHEMA;
